/* @flow */

/*
 * Extending customer
 */

import type { Model } from "crustate";
import type { Data as CustomerData, CustomerRequest as Request, CustomerResponse as Response } from "@crossroads/shop-state/customer";
import type { Customer } from "shop-state/types";

import { updateData } from "crustate";
import { CustomerModel as BaseModel, CUSTOMER_LOGOUT_REQUEST } from "@crossroads/shop-state/customer";

export type CustomerRequest = Request | {
  +tag: typeof CUSTOMER_LOGOUT_REDIRECT_REQUEST,
  +redirectURL: string,
} | {
  +tag: typeof CUSTOMER_EMAIL_VERIFY_REQUEST,
  +memberuserid: string,
} | {
  +tag: typeof CUSTOMER_TWOFA_VERIFY_REQUEST,
  +code: string,
  +remember: boolean,
} | {
  +tag: typeof CUSTOMER_TWOFA_CANCEL_REQUEST,
};
export type Data = CustomerData | {|
  +state: "REDIRECTING",
|} | {|
  +state: "NEED_EMAIL_VERIFICATION",
  +memberuserid: string,
  +email: string,
|} | {|
  +state: "SENDING_EMAIL_VERIFICATION",
|} | {|
  +state: "SENT_EMAIL_VERIFICATION",
  +memberuserid: string,
|} | {|
  +state: "NEED_TWOFA_VERIFICATION",
  +memberuserid: string,
  +email: string,
  +JSESSIONID: string,
  +twofactorSkipDays: number,
|} | {|
  +state: "SENDING_TWOFA_VERIFICATION",
|} | {|
  +state: "SENT_TWOFA_VERIFICATION",
  +data: Customer,
|};

export type CustomerResponse = Response |
{
  +tag: typeof CUSTOMER_LOGOUT_REDIRECT_RESPONSE,
} |
{
  +tag: typeof CUSTOMER_NEED_EMAIL_VERIFY_RESPONSE,
  +memberuserid: string,
  +email: string,
} |
{
  +tag: typeof CUSTOMER_EMAIL_VERIFY_RESPONSE,
  +memberuserid: string,
} |
{
  +tag: typeof CUSTOMER_NEED_TWOFA_VERIFY_RESPONSE,
  +memberuserid: string,
  +email: string,
  +JSESSIONID: string,
  +twofactorSkipDays: number,
} |
{
  +tag: typeof CUSTOMER_TWOFA_VERIFY_RESPONSE,
  +data: ?Customer,
};

export const logout = (redirectURL?: string): | {|
  redirectURL: string,
  tag: typeof CUSTOMER_LOGOUT_REDIRECT_REQUEST,
|} | {| tag: "customer/logout/request" |} => {
  if (redirectURL) {
    return {
      tag: CUSTOMER_LOGOUT_REDIRECT_REQUEST,
      redirectURL,
    };
  }

  return {
    tag: CUSTOMER_LOGOUT_REQUEST,
  };
};

export const sendEmailVerification = (memberuserid: string): {|
  tag: typeof CUSTOMER_EMAIL_VERIFY_REQUEST,
  memberuserid: string,
  |} => {
  return {
    tag: CUSTOMER_EMAIL_VERIFY_REQUEST,
    memberuserid,
  };
};

export const login = (email: string, password: string): {|
  tag: typeof CUSTOMER_LOGIN_REQUEST,
  email: string,
  password: string,
  |} => ({
  tag: CUSTOMER_LOGIN_REQUEST,
  email,
  password,
});

export const loginOtp = (code: string, remember: boolean): {|
  tag: typeof CUSTOMER_TWOFA_VERIFY_REQUEST,
  code: string,
  remember: boolean,
  |} => ({
  tag: CUSTOMER_TWOFA_VERIFY_REQUEST,
  code,
  remember,
});

export const cancelLoginOtp = (): {|
  tag: typeof CUSTOMER_TWOFA_CANCEL_REQUEST,
|} => ({
  tag: CUSTOMER_TWOFA_CANCEL_REQUEST,
});

export const CUSTOMER_LOGIN_REQUEST: "customer/login/request" = "customer/login/request";
export const CUSTOMER_LOGIN_RESPONSE: "customer/login/response" = "customer/login/response";

export const CUSTOMER_LOGOUT_REDIRECT_REQUEST: "customer/logout_redirect/request" = "customer/logout_redirect/request";
export const CUSTOMER_LOGOUT_REDIRECT_RESPONSE: "customer/logout_redirect/response" = "customer/logout_redirect/response";

export const CUSTOMER_EMAIL_VERIFY_REQUEST: "customer/email_verify/request" = "customer/email_verify/request";
export const CUSTOMER_EMAIL_VERIFY_RESPONSE: "customer/email_verify/response" = "customer/email_verify/response";
export const CUSTOMER_NEED_EMAIL_VERIFY_RESPONSE: "customer/need_email_verify/response" = "customer/need_email_verify/response";

export const CUSTOMER_TWOFA_VERIFY_REQUEST: "customer/twofa_verify/request" = "customer/twofa_verify/request";
export const CUSTOMER_TWOFA_VERIFY_RESPONSE: "customer/twofa_verify/response" = "customer/twofa_verify/response";
export const CUSTOMER_NEED_TWOFA_VERIFY_RESPONSE: "customer/need_twofa_verify/response" = "customer/need_twofa_verify/response";

export const CUSTOMER_TWOFA_CANCEL_REQUEST: "customer/twofa_cancel/request" = "customer/twofa_cancel/request";

export const CustomerModel: Model<Data, {}, CustomerRequest | CustomerResponse> = {
  id: "customer",
  init: BaseModel.init,
  update: (state, msg) => {
    switch (msg.tag) {
      case CUSTOMER_LOGOUT_REDIRECT_REQUEST:
        if (state.state === "LOGGED_IN") {
          return updateData({
            state: "LOGGING_OUT",
            data: state.data,
          }, msg);
        }

        throw new Error("Invalid state transition");

      case CUSTOMER_LOGOUT_REDIRECT_RESPONSE:
        return updateData({ state: "REDIRECTING" });

      case CUSTOMER_LOGIN_REQUEST:
        if (state.state === "NOT_LOGGED_IN" || state.state === "SENT_EMAIL_VERIFICATION") {
          return updateData({
            state: "LOGGING_IN",
          }, msg);
        }

        throw new Error("Invalid state transition");

      case CUSTOMER_LOGIN_RESPONSE:
        if (state.state === "LOGGING_IN") {
          return msg.data ?
            updateData({ state: "LOGGED_IN", data: msg.data }) :
            updateData({ state: "NOT_LOGGED_IN" });
        }

        throw new Error("Invalid state transition");

      case CUSTOMER_NEED_EMAIL_VERIFY_RESPONSE:
        return updateData({
          state: "NEED_EMAIL_VERIFICATION",
          memberuserid: msg.memberuserid,
          email: msg.email,
        });

      case CUSTOMER_EMAIL_VERIFY_REQUEST:
        return updateData({
          state: "SENDING_EMAIL_VERIFICATION",
        }, msg);

      case CUSTOMER_EMAIL_VERIFY_RESPONSE:
        return updateData({
          state: "SENT_EMAIL_VERIFICATION",
          memberuserid: msg.memberuserid,
        });

      case CUSTOMER_NEED_TWOFA_VERIFY_RESPONSE:
        return updateData({
          state: "NEED_TWOFA_VERIFICATION",
          memberuserid: msg.memberuserid,
          email: msg.email,
          twofactorSkipDays: msg.twofactorSkipDays,
          JSESSIONID: msg.JSESSIONID,
        });

      case CUSTOMER_TWOFA_VERIFY_REQUEST:
        return updateData({
          state: "SENDING_TWOFA_VERIFICATION",
        }, msg);

      case CUSTOMER_TWOFA_VERIFY_RESPONSE:
        if (state.state === "SENDING_TWOFA_VERIFICATION") {
          return msg.data ?
            updateData({ state: "LOGGED_IN", data: msg.data }) :
            updateData({ state: "NOT_LOGGED_IN" });
        }

        throw new Error("Invalid state transition");

      case CUSTOMER_TWOFA_CANCEL_REQUEST:
        return updateData({ state: "NOT_LOGGED_IN" });

      default:
        if (
          state.state !== "REDIRECTING" &&
          msg.tag !== "customer/need_email_verify/response" &&
          state.state !== "NEED_EMAIL_VERIFICATION" &&
          state.state !== "SENDING_EMAIL_VERIFICATION" &&
          state.state !== "SENT_EMAIL_VERIFICATION" &&
          msg.tag !== "customer/email_verify/request" &&
          msg.tag !== "customer/email_verify/response" &&
          msg.tag !== "customer/need_twofa_verify/response" &&
          state.state !== "NEED_TWOFA_VERIFICATION" &&
          state.state !== "SENDING_TWOFA_VERIFICATION" &&
          state.state !== "SENT_TWOFA_VERIFICATION" &&
          msg.tag !== "customer/twofa_cancel/request" &&
          msg.tag !== "customer/twofa_verify/request" &&
          msg.tag !== "customer/twofa_verify/response"
        ) {
          return BaseModel.update(state, msg);
        }
    }
  },
};
